import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subject, BehaviorSubject, from } from 'rxjs';
import { switchMap, take, tap } from 'rxjs/operators';

import {
  FileModel,
  FileUploadCompleteEvent,
  FileUploadDeleteEvent,
  FileUploadErrorEvent,
  UploadFilesEvent,
  UploadService,
} from '@alfresco/adf-content-services';
import { Node } from '@alfresco/js-api';

import { NotificationDialogComponent } from '../dialogs/notification/notification.dialog';
import { WkFileUploadComponent } from '../dialogs/wk-file-upload/wk-file-upload.component';
import { WkAlfrescoApiService } from './wk-alfresco-api.service';

import { Store } from '@ngrx/store';
import {
  ReloadDocumentListAction,
  SnackbarErrorAction,
  SnackbarInfoAction,
} from '../store/actions/app.custom-actions';

import { RequestParams } from '../types';
import { getCurrentFolder } from '../store/selectors/app.custom-selectors';

@Injectable({
  providedIn: 'root',
})
export class WKFileUploadService {
  filesToBeUploaded = new Subject<string[]>();
  triggerUploadDialog = new Subject<any>();
  fileNamesToUpload: string[];
  uploadedFromSideNav = false;
  fileUploadedFromSideNav = [];
  _fileDetails = new BehaviorSubject<any>('');

  constructor(
    private readonly dialogRef: MatDialog,
    private readonly uploadService: UploadService,
    private readonly apiService: WkAlfrescoApiService,
    private readonly store: Store
  ) {}

  get fileUploadError(): Subject<FileUploadErrorEvent> {
    return this.uploadService.fileUploadError;
  }

  get fileUploadComplete(): Observable<FileUploadCompleteEvent> {
    return this.uploadService.fileUploadComplete.asObservable();
  }

  get fileUploadDeleted(): Observable<FileUploadDeleteEvent> {
    return this.uploadService.fileUploadDeleted.asObservable();
  }

  isUploading(): boolean {
    return this.uploadService.isUploading();
  }

  onDialog(
    node: any,
    ev: UploadFilesEvent,
    files: FileModel[],
    docType: any
  ): Observable<any> {
    return this.dialogRef
      .open(WkFileUploadComponent, {
        data: {
          fileEvent: ev,
          directoryName: node,
          files: ev?.files || files,
          fileNamesToUpload: this.uploadedFromSideNav
            ? this.fileUploadedFromSideNav
            : this.fileNamesToUpload,
          documentType: docType,
        },
        width: '739px',
        height: '419px',
      })
      .afterClosed()
      .pipe(tap(() => this.clearData()));
  }

  showDuplicateDialog(
    duplicates: any[],
    multiple: boolean
  ): MatDialogRef<NotificationDialogComponent> {
    return this.dialogRef.open(NotificationDialogComponent, {
      data: {
        multipleFiles: multiple,
        duplicateFiles: duplicates,
      },
      width: '600px',
      maxWidth: '600px',
    });
  }

  clearData() {
    this.fileNamesToUpload = [];
  }

  beginUpload(ev: UploadFilesEvent, node: Node) {
    ev.pauseUpload();
    ev.files.forEach((file) => (file.options.properties = Object.create(null)));
    this.showUploadDialog(ev.files, node, ev, null);
  }

  showUploadDialog(
    files: Array<FileModel>,
    directory: Node,
    fileEvent: UploadFilesEvent,
    docType: string
  ) {
    return this.dialogRef
      .open(WkFileUploadComponent, {
        data: {
          files,
          directory,
          fileEvent,
          docType,
          fileNamesToUpload: files.map((file) => file.name),
        },
        width: '739px',
        height: '419px',
      })
      .afterClosed();
  }

  uploadZipFile(file: FileModel[]) {
    this.store
      .select(getCurrentFolder)
      .pipe(
        take(1),
        switchMap((node) => {
          const payload: RequestParams = {
            path: `alfresco/s/wk/ui/import?targetNodeRef=workspace://SpacesStore/${node.id}`,
            httpMethod: 'POST',
            contentTypes: ['multipart/form-data'],
            accepts: ['application/json'],
            formParams: { file: file[0].file },
          };
          return from(this.apiService.callContentClientCustomApi(payload));
        })
      )
      .subscribe({
        next: () => {
          this.store.dispatch(
            new SnackbarInfoAction(
              'APP.MESSAGES.INFO.UPLOADED_SUCCESSFULLY_PACKAGE'
            )
          );
          this.store.dispatch(new ReloadDocumentListAction());
        },
        error: () => {
          this.store.dispatch(
            new SnackbarErrorAction(
              'APP.MESSAGES.UPLOAD.ERROR.UPLOADED_UNSUCCESSFULLY_PACKAGE'
            )
          );
        },
      });
  }
}
